/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { clone } from 'ramda';
import { useHistory } from 'react-router-dom';
import { DataGrid, CardsGrid } from 'quantux-ui';
import AddPayerDrawer from '../payers-data-grid/add-payer-drawer';
import CropperDrawer from '../patient-dashboard/cropper-drawer';
import { blue, gray, salmon } from '../../theme';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import PayerCardLarge from '../payer-dashboard/payer-card';
import { BASE_URL, DRAWER_BREAK_POINT } from '../../utils';

const DOCS_PER_PAGE = 50;

const defaultCellStyle = {
  font: '12px Lato',
  // padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const payerIcon = (
  <svg
    width="64"
    height="64"
    style={{ minWidth: '64px', minHeight: '64px' }}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 4a4 4 0 0 1 4-4h56a4 4 0 0 1 4 4v56a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm32.195 1.333L9.337 12.952v22.857c0 12.625 10.233 22.857 22.858 22.857S55.052 48.434 55.052 35.81V12.952l-22.857-7.62Zm.001 49.048c10.256 0 18.572-8.316 18.572-18.572V16.166L32.196 9.62l-18.571 6.547V35.81c0 10.256 8.316 18.572 18.571 18.572Z"
      fill="#00A0FF"
    />
  </svg>
);

const headerDefs = [
  // Payer Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    isPinnedOnMobile: true,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Payer Name',
    field: 'payerName',
    key: 'payerName',

    isCustomFilter: true,
    showFilterHint: true,
    filterHint: 'Search',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    basicFilterType: 'startsWith', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left', menuWidth: 300 }, // 332
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const tmp = text.split('|');
      return (
        <div
          className='fx'
          style={{ fontSize: '14px', fontWeight: '700' }}
        >
          {tmp[1].length === 0
            ? payerIcon
            : <img src={tmp[1]} style={{ height: '64px', width: 'auto' }} />
          }
          <div className='ml-2 pt-2 cursor__pointer'>
            <div className='line-clamp_2'>
              {tmp[0]}
            </div>
          </div>
        </div>
      );
    }
  },
  // Payer ID#
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'ID#',
    field: 'payerId',
    key: 'payerId',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'startsWith',
    disabledFilterTypes: ['contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: {
      width: 198,
      minWidth: 198,
      maxWidth: 198,
      textAlign: 'left'
    },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left', fontSize: '14px', fontWeight: '700', paddingLeft: '8px' },
    footerCellStyle: {}
  },
  // Address
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Mailing Address',
    field: 'address',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 270, minWidth: 270, maxWidth: 270, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      if (!text || text.trim().length <= 1 || text === '||||') {
        return (
          <div
            className='p-2 fx fx__v-center'
            style={{ fontSize: '14px', fontWeight: '700' }}
          >
            <svg
              width="18"
              height="21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.263 4.862a8.149 8.149 0 0 0-1.975-2.535A9.39 9.39 0 0 0 12.422.624 9.659 9.659 0 0 0 9.002 0h-.004c-1.17 0-2.32.21-3.42.624a9.39 9.39 0 0 0-2.866 1.703A8.14 8.14 0 0 0 .737 4.862 7.003 7.003 0 0 0 0 7.978c0 1.497.364 3.035 1.083 4.574.594 1.273 1.435 2.557 2.502 3.819 1.908 2.257 4.056 3.873 4.89 4.461a.905.905 0 0 0 1.05 0c.834-.588 2.984-2.204 4.89-4.461 1.064-1.262 1.908-2.546 2.502-3.82C17.636 11.014 18 9.473 18 7.979a6.996 6.996 0 0 0-.737-3.116Zm-8.264.685c-1.659 0-3.007 1.361-3.007 3.037S7.34 11.621 9 11.621c1.66 0 3.007-1.361 3.007-3.037s-1.348-3.037-3.007-3.037Zm0 7.734a4.599 4.599 0 0 0 3.291-1.376 4.693 4.693 0 0 0 1.362-3.323 4.693 4.693 0 0 0-1.362-3.324A4.606 4.606 0 0 0 9 3.883a4.599 4.599 0 0 0-3.29 1.375 4.696 4.696 0 0 0-1.362 3.324c0 1.257.484 2.437 1.361 3.323A4.603 4.603 0 0 0 9 13.281Z"
                fill="#C0E0FF"
              />
            </svg>
            <div className='ml-2' style={{ color: salmon[500] }}>
              Address
            </div>
          </div>
        );
      }
      const addTmp = text.split('|');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);

      // Generate Google map url
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div
          className="pl-2 fx fx__v-center"
          style={{ fontSize: '14px', fontWeight: '700' }}
        >
          <svg
            width="18"
            height="21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M17.263 4.862a8.149 8.149 0 0 0-1.975-2.535A9.39 9.39 0 0 0 12.422.624 9.659 9.659 0 0 0 9.002 0h-.004c-1.17 0-2.32.21-3.42.624a9.39 9.39 0 0 0-2.866 1.703A8.14 8.14 0 0 0 .737 4.862 7.003 7.003 0 0 0 0 7.978c0 1.497.364 3.035 1.083 4.574.594 1.273 1.435 2.557 2.502 3.819 1.908 2.257 4.056 3.873 4.89 4.461a.905.905 0 0 0 1.05 0c.834-.588 2.984-2.204 4.89-4.461 1.064-1.262 1.908-2.546 2.502-3.82C17.636 11.014 18 9.473 18 7.979a6.996 6.996 0 0 0-.737-3.116Zm-8.264.685c-1.659 0-3.007 1.361-3.007 3.037S7.34 11.621 9 11.621c1.66 0 3.007-1.361 3.007-3.037s-1.348-3.037-3.007-3.037Zm0 7.734a4.599 4.599 0 0 0 3.291-1.376 4.693 4.693 0 0 0 1.362-3.323 4.693 4.693 0 0 0-1.362-3.324A4.606 4.606 0 0 0 9 3.883a4.599 4.599 0 0 0-3.29 1.375 4.696 4.696 0 0 0-1.362 3.324c0 1.257.484 2.437 1.361 3.323A4.603 4.603 0 0 0 9 13.281Z"
              fill="#C0E0FF"
            />
          </svg>
          <div className='ml-2'>
            <a
              target="_blank"
              href={addressUrl}
              rel="noreferrer"
              className="mt-4"
            >
              <div style={{ fontSize: '14px', fontWeight: '700' }}>
                {`${addTmp.slice(0, 2).join(' ')}\n\n`}
                {`${addTmp[2]}, ${addTmp[3]} ${zip}`}
              </div>
            </a>
          </div>
        </div>
      );
    }
  },
  // Phone / Fax
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Phone/Fax',
    field: 'phone',
    key: 'phone_fax',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 233, minWidth: 233, maxWidth: 233, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // const tmp = text.replaceAll('-', '');
      if (!text || text.trim().length <= 1) {
        return (
          <div>
            {/* Phone */}
            <div
              className='p-2 fx'
              style={{ fontSize: '14px' }}
            >
              {/* <i style={{ fontSize: '16px' }} className='fa fa-phone mr-2' /> */}
              <svg
                width="21"
                height="20"
                fill="none"
                className='mr-2'
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16.89 4.485c.163.375.295.766.39 1.166 1.61.491 2.724 2.011 2.727 3.735a3.877 3.877 0 0 1-1.146 2.757c-.4.402-.866.706-1.384.9a7.238 7.238 0 0 1-.585 2.472 7.294 7.294 0 0 1-1.616 2.337 7.5 7.5 0 0 1-2.391 1.572 7.582 7.582 0 0 1-2.811.576h-.116a2.578 2.578 0 0 1-1.802-.777 2.577 2.577 0 0 1-.746-1.82c0-.692.27-1.344.761-1.835a2.579 2.579 0 0 1 1.836-.761h.067c.667.018 1.295.29 1.768.766a2.588 2.588 0 0 1 .721 2.261 5.877 5.877 0 0 0 1.603-1.116 5.658 5.658 0 0 0 1.733-4.075V7.357a5.658 5.658 0 0 0-1.733-4.075 6.004 6.004 0 0 0-4.21-1.697c-1.59 0-3.086.603-4.211 1.7a5.658 5.658 0 0 0-1.733 4.074l.034 5.07a.779.779 0 0 1-.228.563.787.787 0 0 1-.728.218 3.873 3.873 0 0 1-1.945-1.062A3.886 3.886 0 0 1 0 9.388C0 8.533.268 7.72.775 7.04a3.846 3.846 0 0 1 1.853-1.36 7.295 7.295 0 0 1 2.016-3.532A7.502 7.502 0 0 1 7.035.576 7.655 7.655 0 0 1 9.958 0c1.011 0 1.996.194 2.924.576.896.37 1.702.9 2.392 1.572a7.308 7.308 0 0 1 1.616 2.337ZM.395 10.641Zm.222.496c.054.069.11.138.167.205-.057-.067-.113-.134-.167-.205Zm1.813.034a2.305 2.305 0 0 1-.844-1.784c0-.413.107-.82.308-1.174.138-.246.322-.462.536-.639v3.597Zm.2.393.084.083a1.416 1.416 0 0 1-.167-.167l.083.084Zm.147-5.745c.052-.096.11-.192.167-.288a2.73 2.73 0 0 0-.167.288Zm.125 7.161a2.102 2.102 0 0 0 .068.047l-.068-.046Zm1.093-.517v-.033c.01.01.01.022 0 .033ZM5.43 1.706c.043-.033.085-.067.129-.099a3.59 3.59 0 0 0-.13.099Zm.129-.099ZM7.154.723Zm1.276.995.058-.058a1.41 1.41 0 0 1 .116-.11c-.037.038-.077.075-.116.112l-.058.056Zm6.719.56Zm.125.124-.125-.125c.042.04.085.083.125.125Zm.374.41Zm-4.921 15.3a1.004 1.004 0 0 1-.688.305H9.99a1.012 1.012 0 1 1 .737-.306Zm.544-.738a.081.081 0 0 1-.008-.03c.017.02.017.04.017.06 0-.01-.004-.02-.009-.03Zm1.117.814.083-.053a1.934 1.934 0 0 1-.166.106l.083-.053Zm2.668-.373.092-.088c.042-.04.085-.083.125-.125l-.125.125-.136.13c.014-.015.029-.029.044-.042Zm1.048-10.588c0 .02.004.043.008.064a.348.348 0 0 1 .008.065c0-.042 0-.087-.016-.13Zm.622 8.215Zm0 0a2.539 2.539 0 0 0 .024-.057l-.024.057Zm-.148-11.2Zm.21.469a1.828 1.828 0 0 0-.063-.152c.023.05.043.1.063.152Zm.31.945.166.167-.083-.083a9.59 9.59 0 0 0-.083-.084Zm.767 5.496Zm-.128-.127c-.08.08-.163.152-.253.219v-3.74c.24.179.445.404.601.667.219.367.333.786.333 1.217a2.29 2.29 0 0 1-.681 1.637Zm2.002-2.75.049.052c-.056-.057-.111-.113-.167-.167.044.038.081.077.118.116Zm-.29-.533c.041.038.079.076.116.115l-.049-.048-.067-.067Zm.116.115.05.052-.037-.039-.013-.013Zm.188 2.644c-.043.044-.086.09-.134.133l-.002.002.002-.002a1.474 1.474 0 0 0 .134-.133Z"
                  fill="#C0E0FF"
                />
              </svg>
              <div style={{ color: salmon[500] }}>
                Phone
              </div>
            </div>
            {/* Fax */}
            <div
              className='p-2 fx'
              style={{ fontSize: '14px', marginTop: '-4px' }}
            >
              {/* <i style={{ fontSize: '16px' }} className='fa fa-fax mr-2' /> */}
              <svg
                width="20"
                height="17"
                fill="none"
                className='mr-2'
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 5h-1V0H6v5h-.78C4.67 4.39 3.89 4 3 4 1.34 4 0 5.34 0 7v7c0 1.66 1.34 3 3 3 .89 0 1.67-.39 2.22-1H20V8c0-1.66-1.34-3-3-3ZM4 14c0 .55-.45 1-1 1s-1-.45-1-1V7c0-.55.45-1 1-1s1 .45 1 1v7ZM8 2h6v3H8V2Zm10 12H6V7h11c.55 0 1 .45 1 1v6Z" fill="#C0E0FF" /><path d="M13 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM16 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM13 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM16 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM11 8H7v5h4V8Z"
                    fill="#C0E0FF"
                />
              </svg>
              <div style={{ color: salmon[500] }}>
                Fax
              </div>
            </div>
          </div>
        );
      }
      const dataTmp = text.split('|');
      const phoneTmp = dataTmp[0].trim().replace(/-/g, '');
      const faxTmp = dataTmp[1].trim().replace(/-/g, '');
      return (
        <div className='p-2'>
          {/* Phone */}
          <div className='fx fx__v-center'>
            <svg
              width="21"
              height="20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.89 4.485c.163.375.295.766.39 1.166 1.61.491 2.724 2.011 2.727 3.735a3.877 3.877 0 0 1-1.146 2.757c-.4.402-.866.706-1.384.9a7.238 7.238 0 0 1-.585 2.472 7.294 7.294 0 0 1-1.616 2.337 7.5 7.5 0 0 1-2.391 1.572 7.582 7.582 0 0 1-2.811.576h-.116a2.578 2.578 0 0 1-1.802-.777 2.577 2.577 0 0 1-.746-1.82c0-.692.27-1.344.761-1.835a2.579 2.579 0 0 1 1.836-.761h.067c.667.018 1.295.29 1.768.766a2.588 2.588 0 0 1 .721 2.261 5.877 5.877 0 0 0 1.603-1.116 5.658 5.658 0 0 0 1.733-4.075V7.357a5.658 5.658 0 0 0-1.733-4.075 6.004 6.004 0 0 0-4.21-1.697c-1.59 0-3.086.603-4.211 1.7a5.658 5.658 0 0 0-1.733 4.074l.034 5.07a.779.779 0 0 1-.228.563.787.787 0 0 1-.728.218 3.873 3.873 0 0 1-1.945-1.062A3.886 3.886 0 0 1 0 9.388C0 8.533.268 7.72.775 7.04a3.846 3.846 0 0 1 1.853-1.36 7.295 7.295 0 0 1 2.016-3.532A7.502 7.502 0 0 1 7.035.576 7.655 7.655 0 0 1 9.958 0c1.011 0 1.996.194 2.924.576.896.37 1.702.9 2.392 1.572a7.308 7.308 0 0 1 1.616 2.337ZM.395 10.641Zm.222.496c.054.069.11.138.167.205-.057-.067-.113-.134-.167-.205Zm1.813.034a2.305 2.305 0 0 1-.844-1.784c0-.413.107-.82.308-1.174.138-.246.322-.462.536-.639v3.597Zm.2.393.084.083a1.416 1.416 0 0 1-.167-.167l.083.084Zm.147-5.745c.052-.096.11-.192.167-.288a2.73 2.73 0 0 0-.167.288Zm.125 7.161a2.102 2.102 0 0 0 .068.047l-.068-.046Zm1.093-.517v-.033c.01.01.01.022 0 .033ZM5.43 1.706c.043-.033.085-.067.129-.099a3.59 3.59 0 0 0-.13.099Zm.129-.099ZM7.154.723Zm1.276.995.058-.058a1.41 1.41 0 0 1 .116-.11c-.037.038-.077.075-.116.112l-.058.056Zm6.719.56Zm.125.124-.125-.125c.042.04.085.083.125.125Zm.374.41Zm-4.921 15.3a1.004 1.004 0 0 1-.688.305H9.99a1.012 1.012 0 1 1 .737-.306Zm.544-.738a.081.081 0 0 1-.008-.03c.017.02.017.04.017.06 0-.01-.004-.02-.009-.03Zm1.117.814.083-.053a1.934 1.934 0 0 1-.166.106l.083-.053Zm2.668-.373.092-.088c.042-.04.085-.083.125-.125l-.125.125-.136.13c.014-.015.029-.029.044-.042Zm1.048-10.588c0 .02.004.043.008.064a.348.348 0 0 1 .008.065c0-.042 0-.087-.016-.13Zm.622 8.215Zm0 0a2.539 2.539 0 0 0 .024-.057l-.024.057Zm-.148-11.2Zm.21.469a1.828 1.828 0 0 0-.063-.152c.023.05.043.1.063.152Zm.31.945.166.167-.083-.083a9.59 9.59 0 0 0-.083-.084Zm.767 5.496Zm-.128-.127c-.08.08-.163.152-.253.219v-3.74c.24.179.445.404.601.667.219.367.333.786.333 1.217a2.29 2.29 0 0 1-.681 1.637Zm2.002-2.75.049.052c-.056-.057-.111-.113-.167-.167.044.038.081.077.118.116Zm-.29-.533c.041.038.079.076.116.115l-.049-.048-.067-.067Zm.116.115.05.052-.037-.039-.013-.013Zm.188 2.644c-.043.044-.086.09-.134.133l-.002.002.002-.002a1.474 1.474 0 0 0 .134-.133Z"
                fill="#C0E0FF"
              />
            </svg>
            <a
              className='ml-2'
              href={`tel:+1${phoneTmp}`}
              style={{ fontSize: '14px', fontWeight: '700' }}
            >
              {`(${phoneTmp.substring(0, 3)}) ${phoneTmp.substring(3, 6)}-${phoneTmp.substring(6)}`}
            </a>
          </div>
          {/* Fax */}
          <div className='mt-3 fx fx__v-center'>
            <svg
              width="20"
              height="17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 5h-1V0H6v5h-.78C4.67 4.39 3.89 4 3 4 1.34 4 0 5.34 0 7v7c0 1.66 1.34 3 3 3 .89 0 1.67-.39 2.22-1H20V8c0-1.66-1.34-3-3-3ZM4 14c0 .55-.45 1-1 1s-1-.45-1-1V7c0-.55.45-1 1-1s1 .45 1 1v7ZM8 2h6v3H8V2Zm10 12H6V7h11c.55 0 1 .45 1 1v6Z" fill="#C0E0FF" /><path d="M13 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM16 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM13 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM16 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM11 8H7v5h4V8Z"
                  fill="#C0E0FF"
              />
            </svg>
            <a
              className='ml-2'
              href={`tel:+1${faxTmp}`}
              style={{ fontSize: '14px', fontWeight: '700' }}
            >
              {`(${faxTmp.substring(0, 3)}) ${faxTmp.substring(3, 6)}-${faxTmp.substring(6)}`}
            </a>
          </div>
        </div>
      );
    }
  },
  // Website / Email
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Website/Email',
    field: 'websiteEmail',
    key: 'website_email',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 371, minWidth: 371, maxWidth: 371, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      if (!text || text.trim().length <= 1) {
        return (
          <div className='p-2'>
            {/* Website */}
            <div
              className='fx'
              style={{ fontSize: '14px' }}
            >
              {/* <i style={{ fontSize: '16px' }} className='fa fa-globe mr-2' /> */}
              <svg
                width="20"
                height="20"
                fill="none"
                className='mr-2'
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 10C0 4.48 4.379 0 9.786 0c5.417 0 9.806 4.48 9.806 10s-4.389 10-9.806 10C4.38 20 0 15.52 0 10Zm13.684-3.999h2.89a7.904 7.904 0 0 0-4.242-3.56 15.846 15.846 0 0 1 1.352 3.56ZM9.797 2.04c.813 1.2 1.45 2.53 1.87 3.96H7.927a14.214 14.214 0 0 1 1.87-3.96ZM1.96 10c0 .69.098 1.36.255 2h3.31c-.078-.66-.136-1.32-.136-2 0-.68.058-1.34.137-2H2.216a8.404 8.404 0 0 0-.255 2Zm1.055 4h2.89c.313 1.25.764 2.45 1.351 3.56A7.862 7.862 0 0 1 3.016 14Zm0-7.999h2.89c.313-1.25.764-2.45 1.351-3.56a7.862 7.862 0 0 0-4.241 3.56Zm6.78 11.959c-.812-1.2-1.449-2.53-1.87-3.96h3.742a14.212 14.212 0 0 1-1.871 3.96ZM7.505 12h4.585c.088-.66.157-1.32.157-2 0-.68-.069-1.35-.157-2H7.504c-.088.65-.156 1.32-.156 2 0 .68.068 1.34.156 2Zm4.828 5.56A15.846 15.846 0 0 0 13.684 14h2.89a7.904 7.904 0 0 1-4.242 3.56ZM14.203 10c0 .68-.058 1.34-.137 2h3.311a8.4 8.4 0 0 0 .255-2 8.4 8.4 0 0 0-.255-2h-3.31c.078.66.136 1.32.136 2Z"
                  fill="#C0E0FF"
                />
              </svg>
              <div style={{ color: salmon[500] }}>
                Website
              </div>
            </div>
            {/* Email */}
            <div
              className='fx mt-3'
              style={{ fontSize: '14px', marginTop: '-4px' }}
            >
              <svg
                width="16"
                height="17"
                fill="none"
                className='mr-2'
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.38 5.98a3.574 3.574 0 0 0-.66-.06c-.413 0-.8.09-1.16.27-.36.18-.673.423-.94.73-.26.306-.467.66-.62 1.06-.153.393-.23.803-.23 1.23 0 .426.09.76.27 1 .187.233.457.35.81.35.173 0 .347-.027.52-.08.18-.054.35-.147.51-.28.16-.14.307-.324.44-.55.133-.227.243-.51.33-.85l.73-2.82Zm1.17 2.56c-.107.426-.16.776-.16 1.05.007.266.05.48.13.64a.66.66 0 0 0 .34.32c.147.06.313.09.5.09.28 0 .543-.087.79-.26.253-.18.473-.43.66-.75.187-.32.333-.7.44-1.14.113-.44.17-.92.17-1.44 0-.867-.137-1.63-.41-2.29a4.415 4.415 0 0 0-1.13-1.65 4.69 4.69 0 0 0-1.74-1 6.798 6.798 0 0 0-2.2-.34c-.88 0-1.693.166-2.44.5a6.051 6.051 0 0 0-1.95 1.35 6.321 6.321 0 0 0-1.29 2.03c-.307.78-.46 1.62-.46 2.52 0 1.093.17 2.056.51 2.89.34.826.807 1.52 1.4 2.08.593.553 1.29.97 2.09 1.25.807.28 1.677.42 2.61.42.513 0 .993-.03 1.44-.09.447-.054.857-.13 1.23-.23a7.328 7.328 0 0 0 1.02-.33c.307-.12.58-.25.82-.39.173-.1.327-.13.46-.09.133.04.23.14.29.3l.34.89c-.693.453-1.503.823-2.43 1.11-.927.286-1.983.43-3.17.43-1.207 0-2.323-.19-3.35-.57a7.742 7.742 0 0 1-2.67-1.64c-.747-.707-1.333-1.57-1.76-2.59C.21 10.59 0 9.443 0 8.17c0-.714.09-1.407.27-2.08a8.03 8.03 0 0 1 .79-1.89 8.114 8.114 0 0 1 2.83-2.9 7.797 7.797 0 0 1 1.9-.82 7.65 7.65 0 0 1 2.15-.3c.947 0 1.853.16 2.72.48a6.985 6.985 0 0 1 2.31 1.36 6.461 6.461 0 0 1 1.61 2.16c.4.846.6 1.803.6 2.87 0 .713-.107 1.383-.32 2.01-.207.62-.5 1.16-.88 1.62-.373.46-.817.823-1.33 1.09-.507.26-1.06.39-1.66.39-.46 0-.867-.107-1.22-.32-.353-.214-.597-.557-.73-1.03-.393.48-.81.823-1.25 1.03a3.32 3.32 0 0 1-1.43.31c-.42 0-.787-.074-1.1-.22a2.31 2.31 0 0 1-.78-.6 2.741 2.741 0 0 1-.47-.91 4.21 4.21 0 0 1-.15-1.15c0-.38.047-.764.14-1.15.1-.394.247-.77.44-1.13.193-.367.433-.707.72-1.02.293-.32.633-.597 1.02-.83.393-.24.833-.427 1.32-.56a5.919 5.919 0 0 1 1.62-.21c.52 0 .963.04 1.33.12.367.08.72.193 1.06.34l-.96 3.71Z"
                  fill="#C0E0FF"
                />
              </svg>
              <div style={{ color: salmon[500], paddingLeft: '4px' }}>
                Email
              </div>
            </div>
          </div>
        );
      }

      const tmp = text.split('|');
      // const tmpWebsite = tmp[0].replace(/-/g, '');
      const tmpWebsite = tmp[0];
      const tmpEmail = tmp[1];
      return (
        <div className='p-2'>
          {/* Website */}
          <div className='fx'>
            <svg
              width="20"
              height="20"
              fill="none"
              className='mr-2'
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 10C0 4.48 4.379 0 9.786 0c5.417 0 9.806 4.48 9.806 10s-4.389 10-9.806 10C4.38 20 0 15.52 0 10Zm13.684-3.999h2.89a7.904 7.904 0 0 0-4.242-3.56 15.846 15.846 0 0 1 1.352 3.56ZM9.797 2.04c.813 1.2 1.45 2.53 1.87 3.96H7.927a14.214 14.214 0 0 1 1.87-3.96ZM1.96 10c0 .69.098 1.36.255 2h3.31c-.078-.66-.136-1.32-.136-2 0-.68.058-1.34.137-2H2.216a8.404 8.404 0 0 0-.255 2Zm1.055 4h2.89c.313 1.25.764 2.45 1.351 3.56A7.862 7.862 0 0 1 3.016 14Zm0-7.999h2.89c.313-1.25.764-2.45 1.351-3.56a7.862 7.862 0 0 0-4.241 3.56Zm6.78 11.959c-.812-1.2-1.449-2.53-1.87-3.96h3.742a14.212 14.212 0 0 1-1.871 3.96ZM7.505 12h4.585c.088-.66.157-1.32.157-2 0-.68-.069-1.35-.157-2H7.504c-.088.65-.156 1.32-.156 2 0 .68.068 1.34.156 2Zm4.828 5.56A15.846 15.846 0 0 0 13.684 14h2.89a7.904 7.904 0 0 1-4.242 3.56ZM14.203 10c0 .68-.058 1.34-.137 2h3.311a8.4 8.4 0 0 0 .255-2 8.4 8.4 0 0 0-.255-2h-3.31c.078.66.136 1.32.136 2Z"
                fill="#C0E0FF"
              />
            </svg>
            <a
              href={tmpWebsite.startsWith('http') ? tmpWebsite : `http://${tmpWebsite}`}
              target='_blank'
              rel='noreferrer'
              style={{ fontSize: '14px', fontWeight: '700', maxWidth: '340px' }}
            >
              <div
                className='text-truncate'
                style={{ width: '320px', maxWidth: '320px' }}
              >
                {tmpWebsite}
              </div>
            </a>
          </div>
          {/* Email */}
          <div className='mt-3 fx'>
            {/* <i style={{ fontSize: '16px' }} className='fa fa-envelope mr-2' /> */}
            <svg
              width="16"
              height="17"
              fill="none"
              className='mr-2'
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.38 5.98a3.574 3.574 0 0 0-.66-.06c-.413 0-.8.09-1.16.27-.36.18-.673.423-.94.73-.26.306-.467.66-.62 1.06-.153.393-.23.803-.23 1.23 0 .426.09.76.27 1 .187.233.457.35.81.35.173 0 .347-.027.52-.08.18-.054.35-.147.51-.28.16-.14.307-.324.44-.55.133-.227.243-.51.33-.85l.73-2.82Zm1.17 2.56c-.107.426-.16.776-.16 1.05.007.266.05.48.13.64a.66.66 0 0 0 .34.32c.147.06.313.09.5.09.28 0 .543-.087.79-.26.253-.18.473-.43.66-.75.187-.32.333-.7.44-1.14.113-.44.17-.92.17-1.44 0-.867-.137-1.63-.41-2.29a4.415 4.415 0 0 0-1.13-1.65 4.69 4.69 0 0 0-1.74-1 6.798 6.798 0 0 0-2.2-.34c-.88 0-1.693.166-2.44.5a6.051 6.051 0 0 0-1.95 1.35 6.321 6.321 0 0 0-1.29 2.03c-.307.78-.46 1.62-.46 2.52 0 1.093.17 2.056.51 2.89.34.826.807 1.52 1.4 2.08.593.553 1.29.97 2.09 1.25.807.28 1.677.42 2.61.42.513 0 .993-.03 1.44-.09.447-.054.857-.13 1.23-.23a7.328 7.328 0 0 0 1.02-.33c.307-.12.58-.25.82-.39.173-.1.327-.13.46-.09.133.04.23.14.29.3l.34.89c-.693.453-1.503.823-2.43 1.11-.927.286-1.983.43-3.17.43-1.207 0-2.323-.19-3.35-.57a7.742 7.742 0 0 1-2.67-1.64c-.747-.707-1.333-1.57-1.76-2.59C.21 10.59 0 9.443 0 8.17c0-.714.09-1.407.27-2.08a8.03 8.03 0 0 1 .79-1.89 8.114 8.114 0 0 1 2.83-2.9 7.797 7.797 0 0 1 1.9-.82 7.65 7.65 0 0 1 2.15-.3c.947 0 1.853.16 2.72.48a6.985 6.985 0 0 1 2.31 1.36 6.461 6.461 0 0 1 1.61 2.16c.4.846.6 1.803.6 2.87 0 .713-.107 1.383-.32 2.01-.207.62-.5 1.16-.88 1.62-.373.46-.817.823-1.33 1.09-.507.26-1.06.39-1.66.39-.46 0-.867-.107-1.22-.32-.353-.214-.597-.557-.73-1.03-.393.48-.81.823-1.25 1.03a3.32 3.32 0 0 1-1.43.31c-.42 0-.787-.074-1.1-.22a2.31 2.31 0 0 1-.78-.6 2.741 2.741 0 0 1-.47-.91 4.21 4.21 0 0 1-.15-1.15c0-.38.047-.764.14-1.15.1-.394.247-.77.44-1.13.193-.367.433-.707.72-1.02.293-.32.633-.597 1.02-.83.393-.24.833-.427 1.32-.56a5.919 5.919 0 0 1 1.62-.21c.52 0 .963.04 1.33.12.367.08.72.193 1.06.34l-.96 3.71Z"
                fill="#C0E0FF"
              />
            </svg>
            <a
              href={`mailto:${tmpEmail}`}
              target='_blank'
              rel='noreferrer'
              style={{ fontSize: '14px', fontWeight: '700' }}
            >
              <div
                className='text-truncate'
                style={{ width: '320px', maxWidth: '320px', paddingLeft: '4px' }}
              >
                {tmpEmail}
              </div>
            </a>
          </div>
        </div>
      );
    }
  },
  // Servicing States
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    isPinnedOnMobile: false,
    sortable: true,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Servicing States',
    field: 'servicingStates',
    key: 'servicingStates',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'contains',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['exactMatch', 'endsWith', 'advanced'],
    colStyles: { width: 228, minWidth: 228, maxWidth: 228, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left', paddingLeft: '8px', fontSize: '14px', fontWeight: '700' },
    footerCellStyle: {}
  },
];

const PayersDataGrid2 = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [gridHeaderDefs, setGridHeaderDefs] = useState(headerDefs);
  const [gridData, setGridData] = useState([]);
  const [payerData, setPayerData] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 1300);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 135);
  const [isMobileLayout, setMobileLayout] = useState(false);
  // --
  const [showAddPayerDrawer, setShowAddPayerDrawer] = useState(false);
  const [showAvatarCropperDrawer, setShowAvatarCropperDrawer] = useState(false);
  const [showBannerCropperDrawer, setShowBannerCropperDrawer] = useState(false);
  const [payerBannerImageUrl, setPayerBannerImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
  const [payerAvatarImageUrl, setPayerAvatarImageUrl] = useState('');
  // --
  const [showCardsGrid, setShowCardsGrid] = useState(false);

  const gridParentRef = useRef();

  const history = useHistory();

  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      width: `${gridWidth}px`,
      overflow: 'hidden'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '52px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    }
  });

  const getData = async () => {
    setShowLoader(true);
    const appliedFilters = {};
    const filterKeys = Object.keys(filters);
    filterKeys.forEach(el => {
      if (filters[el].dataType !== 'select') {
        appliedFilters[el] = clone(filters[el]);
      }
    })

    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    formData.append('filters', JSON.stringify(appliedFilters));
    formData.append('sorting', JSON.stringify(sorting));

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/payers2/all`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/payers2/count`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting icd10 grid data and count');
        console.log(err);
      });

    if (response) {
      const payersDataResponse = await response[0].json();
      const payersCountResponse = await response[1].json();

      if (payersDataResponse && payersDataResponse.success) {
        const data = [];
        for (let i = 0; i < payersDataResponse.payers.length; i++) {
          const activeRecord = payersDataResponse.payers[i];
          const add1 = activeRecord.address && activeRecord.address.streetAddress1 ? activeRecord.address.streetAddress1 : '';
          const suite = activeRecord.address && activeRecord.address.suite ? activeRecord.address.suite : '';
          const city = activeRecord.address && activeRecord.address.city ? activeRecord.address.city : ''
          const state = activeRecord.address && activeRecord.address.state ? activeRecord.address.state : ''
          const zip = activeRecord.address && activeRecord.address.zip ? activeRecord.address.zip : '';
          data.push({
            id: activeRecord._id,
            payerName: `${activeRecord.payerName}|${activeRecord.avatarImageUrl || ''}|${activeRecord.userName || 'N/A'}`,
            payerId: activeRecord.payerId,
            address: `${add1}|${suite}|${city}|${state}|${zip}`,
            phone: `${activeRecord.phone || ''} | ${activeRecord.fax || ''}`,
            websiteEmail: `${activeRecord.website || ''}|${activeRecord.email || ''} `,
            servicingStates: activeRecord.servicingStates
          });
        }
        setPayerData(payersDataResponse.payers);
        setGridData(data);
        setShowLoader(false);
      }

      if (payersCountResponse && payersCountResponse.success) {
        console.log(`[Debug] Total payers count = ${payersCountResponse.payersCount}`);
        setDocsCount(payersCountResponse.payersCount);
      }
    }
  };

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.98 * gridParentRefWidth);
      if (window.innerWidth <= 640) {
        setShowCardsGrid(true);
      }
      setGridWidth(Math.min(tmp, 1533)); // 983
      setGridHeight(window.innerHeight - 135);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    if (localStorage.getItem('payersGridData')) {
      let data = JSON.parse(localStorage.getItem('payersGridData'));
      setGridData(data.gridData);
      setPayerData(data.payerData);
      setFilters(data.filters);
      setSorting(data.sorting);
      setShowCardsGrid(data.showCardsGrid);
    } else {
      getData();
    }
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (localStorage.getItem('payersGridData')) {
      localStorage.removeItem('payersGridData');
    } else {
      getData();
    }
  }, [currentPage, filters, sorting])

  const handleOnLoadPage = (page) => {
    setCurrentPage(page);
  };

  const handleOnApplyFilter = (dataType, filterType, field, filterValue) => {
    const updatedFilters = clone(filters);
    if (filterValue !== '' && filterValue !== ':') {
      updatedFilters[field] = {
        dataType,
        filterType,
        value: filterValue
      };
    } else {
      delete updatedFilters[field];
    }

    setFilters(updatedFilters);
  };

  const handleFilterTypeUpdate = (field, value) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    const updatedFilters = clone(filters);
    if (updatedFilters[field]) {
      updatedFilters[field].filterType = value;
      updatedFilters[field].value = value !== 'dateRange' ? '' : ':';
    }
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.filterType = value;
        el.filter = true;
      }
    });
    setGridHeaderDefs(updatedGridHeaderDefs);
    setFilters(updatedFilters);
  };

  const handleOnClickResetFilters = () => {
    setFilters({});
  };

  const handleOnSortOrderChanged = (field) => {
    const updatedGridHeaderDefs = gridHeaderDefs.concat();
    updatedGridHeaderDefs.forEach(el => {
      if (el.field === field) {
        el.sortOrder = el.sortOrder === 0
          ? el.sortOrder + 1
          : el.sortOrder + (-el.sortOrder * 2);
      } else {
        el.sortOrder = 0;
      }
    });

    const sort = {};
    for (let i = 0; i < updatedGridHeaderDefs.length; i++) {
      if (updatedGridHeaderDefs[i].sortOrder === 0) {
        continue;
      } else {
        if (updatedGridHeaderDefs[i].field === 'payerName') {
          // sort['lastName'] = updatedGridHeaderDefs[i].sortOrder;
          sort.payerName = updatedGridHeaderDefs[i].sortOrder;
        } else if (updatedGridHeaderDefs[i].field === 'payerCode') {
          // sort['createdOn'] = updatedGridHeaderDefs[i].sortOrder;
          sort.payerCode = updatedGridHeaderDefs[i].sortOrder;
        } else {
          sort[updatedGridHeaderDefs[i].field] = updatedGridHeaderDefs[i].sortOrder;
        }
      }
    }
    setGridHeaderDefs(updatedGridHeaderDefs);
    setSorting(sort);
  };

  const handleOnClickCell = (col, value, recordId) => {
    if (col === 'payerName') {
      const tmp = value.split('|');
      const idx = gridData.findIndex(el => el.payerName === value);
      const payerId = gridData[idx].id;
      localStorage.setItem('payersGridData', JSON.stringify({
        showCardsGrid,
        payerData,
        gridData,
        filters,
        sorting
      }));
      history.push(`/payer/${payerId}/profile`);
    }
  };

  const handleOnSelectItemChange = (col, val) => {
    const updatedFilters = clone(filters);
    updatedFilters[col].items.forEach(el => {
      if (el.value === val) {
        el.checked = !el.checked;
      }
    });
    setFilters(updatedFilters);
  };

  const handleOnSelectAllChange = (col) => {
    const updatedFilters = clone(filters);
    const checkedItems = updatedFilters[col].items.reduce((prv, curr) => prv.hasOwnProperty('checked') ? (prv.checked ? 1 : 0) + (curr.checked ? 1 : 0) : prv + (curr.checked ? 1 : 0));
    let checkedValue;
    if (checkedItems === 0) {
      checkedValue = true;
    }
    if (checkedItems > 0 && checkedItems < updatedFilters[col].items.length) {
      checkedValue = true;
    }
    if (checkedItems > 0 && checkedItems === updatedFilters[col].items.length) {
      checkedValue = false;
    }

    updatedFilters[col].items.forEach(el => {
      el.checked = checkedValue;
    });

    setFilters(updatedFilters);
  };

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Add Payer Drawer */}
      {showAddPayerDrawer &&
        <AddPayerDrawer
          isOpen={showAddPayerDrawer}
          onClose={() => setShowAddPayerDrawer(false)}
          payerBannerImageUrl={payerBannerImageUrl}
          payerAvatarImageUrl={payerAvatarImageUrl}
          onReloadData={() => {
            setShowAddPayerDrawer(false);
            getData();
          }}
          onClickAvatarCamera={() => {
            setShowAvatarCropperDrawer(true);
          }}
          onClickBannerCamera={() => {
            setShowBannerCropperDrawer(true);
          }}
        />
      }
      {/* Avatar Cropper Drawer */}
      {showAvatarCropperDrawer &&
        <CropperDrawer
          isVisible={showAvatarCropperDrawer}
          placement='custom'
          style={{ position: 'absolute', top: '0', right: '0', zIndex: '2050' }}
          cropperAspectRatio={1}
          headerText='Upload Payer Avatar'
          uploadUserMessage='Uploading Payer Avatar Image'
          cropButtonText='Crop Avatar Image'
          rawFileUploadUrl='users/uploadImg'
          croppedFileUploadUrl='users/uploadImg'
          imageUploadFormData={{}}
          onClose={() => {
            setShowAvatarCropperDrawer(false);
          }}
          onCroppedImageUpload={(url) => {
            console.log(`Cropped file uploaded. URL = ${url}`);
            setPayerAvatarImageUrl(url);
          }}
        />
      }

      {/* Banner Cropper Drawer */}
      {showBannerCropperDrawer &&
        <CropperDrawer
          isVisible={showBannerCropperDrawer}
          placement='custom'
          style={{ position: 'absolute', top: '0', right: '0', zIndex: '2050' }}
          cropperAspectRatio={6}
          headerText='Upload Payer Banner'
          uploadUserMessage='Uploading Payer Banner Image'
          cropButtonText='Crop Banner Image'
          rawFileUploadUrl='users/uploadImg'
          croppedFileUploadUrl='users/uploadImg'
          imageUploadFormData={{}}
          onClose={() => {
            setShowBannerCropperDrawer(false);
          }}
          onCroppedImageUpload={(url) => {
            console.log(`Cropped file uploaded. URL = ${url}`);
            setPayerBannerImageUrl(url);
          }}
        />
      }

      <div className={css(styles.gridContainer)}>
        {/* Grid Toolbar  */}
        <div className={css(styles.gridToolbar)}>
          {/* Cards/Data Toggle Button */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={() => {
              setShowCardsGrid(!showCardsGrid);
            }}
          >
            {/* <i className="fa fa-lg fa-plus-circle" /> */}
            <i className={`fa fa-lg ${showCardsGrid ? 'fa-bars' : 'fa-th-large'}`} />
          </div>
          {/* Add Button */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={() => {
              setPayerAvatarImageUrl('');
              setPayerBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
              setShowAddPayerDrawer(true);
            }}
          >
            <i className="fa fa-lg fa-plus-circle" />
          </div>
          {/* Reset button */}
          <div
            className={`mr-2 ${css(styles.gridToolbarIcon)}`}
            onClick={handleOnClickResetFilters}
          >
            <i className="fa fa-lg fa-refresh" />
          </div>
        </div>
        {/* Data Grid */}
        {!showCardsGrid
          ? <DataGrid
            loader={showLoader}
            icons={icons}
            headerDefs={gridHeaderDefs}
            rowData={gridData}
            filters={filters}
            gridWidth={gridWidth} // was 550
            gridHeight={gridHeight}
            gridHeaderHeight={48}
            gridFooterHeight={0}
            gridContainerHeight='730px'
            rowHeight={80}
            rowsPerPage={DOCS_PER_PAGE}
            defaultCellStyle={defaultCellStyle}
            showFooter={false}
            showPager
            showEllipsisCol
            enableHover
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            isMobileLayout={isMobileLayout}
            canSelectRows={false}
            onLoadPage={handleOnLoadPage}
            onApplyFilter={handleOnApplyFilter}
            onFilterTypeUpdate={handleFilterTypeUpdate}
            onSort={handleOnSortOrderChanged}
            onClickCell={handleOnClickCell}
            onSelectItemChange={handleOnSelectItemChange}
            onSelectAllChange={handleOnSelectAllChange}
            onPopupMenu={() => { }}
            onColFilterToggle={() => { }}
            onColPinChange={() => {
            }}
            onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
            onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
            onResize={() => { console.warn('Handler not set for onResize prop.'); }}
            onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
            onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
            onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
            onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
          />
          : <CardsGrid
            rowData={payerData}
            gridWidth={gridWidth}
            gridHeight={gridHeight}
            loader={showLoader}
            style={{
              // height: 'calc(100% - 73px)',
              height: 'calc(100vh - 175px)',
              maxHeight: 'calc(100vh - 175px)',
              overflowY: 'auto',
              justifyItems: 'center'
            }}
            cardHeight='323px'
            cardWidth='359px'
            totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
            currentPage={currentPage}
            onLoadPage={handleOnLoadPage}
            cardRenderer={(data) => {
              return (
                <PayerCardLarge
                  handleOnClickEllipsis={() => {
                    localStorage.setItem('payersGridData', JSON.stringify({
                      showCardsGrid,
                      payerData,
                      gridData,
                      filters,
                      sorting
                    }));
                    const payerId = data._id;
                    history.push(`/payer/${payerId}/profile`);
                  }}
                  data={data}
                />
              );
            }}
          />
        }
      </div>
    </div>
  );
};

export default PayersDataGrid2;
